import * as React from "react";

import { Alert, Box, Snackbar, Stack, Typography } from "@mui/material";

import { useSectionViewEventTracker } from "@utils/analytics";
import personalRepresentative from "@assets/images/personal-representative.svg";
import {
  NotifierDetails as NotifierDetailsType,
  NotifierFormData,
  Relationship,
  Titles,
} from "../../types/NotifierFormData";
import { filterProperties, validEmailAddress } from "../../utils/Functions";
import { ReactStateSetter } from "../../utils/Types";
import { BooleanInput } from "../../components/atoms/BooleanInput";
import { DateInput } from "../../components/atoms/DateInput";
import { EmailAddressInput } from "../../components/atoms/EmailAddressInput";
import { Header } from "../../components/atoms/Header";
import { PhoneNumberTextInput } from "../../components/atoms/PhoneNumberTextInput";
import { SelectInput } from "../../components/atoms/SelectInput";
import { TextInput } from "../../components/atoms/TextInput";
import { NewTitleInput } from "../../components/atoms/TitleInput";
import { NewAddressInput } from "../../components/molecules/AddressInput";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { InfoBoxTrigger } from "../../components/molecules/InfoBoxTrigger";
import { RelationToDeceased } from "../../components/molecules/RelationToDeceased";
import { Section } from "../../Sections";
import { UpdateFormTemplateProc } from "@templates/FormTemplate";
import { Person } from "./AccountForm/ResponsibleFields";
import { updateNotifier } from "@api/caseApi";
import { roleOptions } from "@customTypes/NotifierFormData";

export type NotifierDetailsPersistedState = {
  record: NotifierRecord;
  hasChanges: boolean;
  errors: Errors;
  remoteError?: string;
};

export type NotifierRecord = {
  relationshipToDeceased?: Relationship;
  relationshipToDeceasedOther?: string;
  title?: Titles;
  firstName?: string;
  surname?: string;
  readonly dateOfBirth?: string;
  address?: string;
  city?: string;
  postcode?: string;
  contactNumber?: string;
  email?: string;
  doNotContact?: boolean;
  role?: string;
  isVulnerable?: boolean;
};

type Errors = Partial<Record<keyof NotifierDetailsType, string>>;

export const notifierDetailsPersistedStateFromForm = (
  form: NotifierFormData
): NotifierDetailsPersistedState => {
  const record = filterProperties(form.notifier || {}, [
    "title",
    "firstName",
    "surname",
    "dateOfBirth",
    "address",
    "city",
    "postcode",
    "email",
    "contactNumber",
    "relationshipToDeceased",
    "relationshipToDeceasedOther",
    "role",
    "isVulnerable",
  ]);

  return {
    record,
    hasChanges: false,
    errors: {},
  };
};

const updatedFormFromPersistedState = (
  form: NotifierFormData,
  state: NotifierDetailsPersistedState
): NotifierFormData => {
  const record = notifierRecordFromPersistedState(state);

  const notifier = !form.notifier
    ? record
    : {
        ...record,
        proofOfAddress: form?.notifier?.proofOfAddress,
      };

  return { ...form, notifier };
};

const notifierRecordFromPersistedState = (state: NotifierDetailsPersistedState) => {
  const { relationshipToDeceasedOther, ...rest } = state.record;

  return rest.relationshipToDeceased === Relationship.Other && relationshipToDeceasedOther
    ? state.record
    : rest;
};

const serverRecordFromPersistedState = (state: NotifierDetailsPersistedState) => {
  const title = state.record.title?.toLowerCase();

  const relationshipToDeceased = state.record.relationshipToDeceased?.toLowerCase();

  return {
    title,
    firstName: state.record.firstName,
    lastName: state.record.surname,
    dateOfBirth: state.record.dateOfBirth,
    address: state.record.address,
    city: state.record.city,
    postcode: state.record.postcode,
    emailAddress: state.record.email,
    phoneNumber: state.record.contactNumber,
    relationshipToDeceased,
    relationshipToDeceasedOther: state.record.relationshipToDeceasedOther,
    role: state.record.role,
    isVulnerable: state.record.isVulnerable,
  };
};

type NotifierDetailsProps = {
  caseId: string;
  signature: string | null;
  prepareUpdate: Function;
  updateSuccessful: Function;
  updateFailure: (error: Error) => void;
  form: NotifierFormData;
  busy: boolean;
  continueWithoutChanges: (section: Section, doNotAdvance?: boolean) => void;
  persistedState: NotifierDetailsPersistedState;
  setPersistedState: ReactStateSetter<NotifierDetailsPersistedState>;
  updatePerson: (p: Person) => void;
  updatingNotifierEmailAddress: boolean;
  setUpdatingNotifierEmailAddress: (a: boolean) => void;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
};

export const NotifierDetails: React.FC<NotifierDetailsProps> = ({
  caseId,
  signature,
  prepareUpdate,
  updateSuccessful,
  updateFailure,
  form,
  busy,
  continueWithoutChanges,
  persistedState,
  setPersistedState,
  updatePerson,
  updatingNotifierEmailAddress,
  onSectionClick,
  updateTemplate,
}) => {
  useSectionViewEventTracker("Notifier details");

  const { record, hasChanges, errors } = persistedState;

  const onFieldChange = React.useCallback(
    ({ target: { name, value } }: { target: { name: string; value: string | boolean } }) => {
      setPersistedState({
        hasChanges: true,
        record: {
          ...record,
          [name]: value,
        },
        errors: {
          ...errors,
          [name]: undefined,
        },
      });
    },
    [record, errors, setPersistedState]
  );

  const onRoleChange = React.useCallback(
    (value) => {
      setPersistedState({
        hasChanges: true,
        record: {
          ...record,
          role: value,
          dateOfBirth: value === "solicitor" ? undefined : record.dateOfBirth,
          isVulnerable: value === "solicitor" ? false : record.isVulnerable,
          relationshipToDeceased:
            value === "solicitor" ? Relationship.Other : record.relationshipToDeceased,
          relationshipToDeceasedOther:
            value === "solicitor" ? "Solicitor" : record.relationshipToDeceased,
        },
        errors: {
          ...errors,
          role: undefined,
          dateOfBirth: undefined,
          isVulnerable: undefined,
          relationshipToDeceased: undefined,
          relationshipToDeceasedOther: undefined,
        },
      });
    },
    [record, errors, setPersistedState]
  );

  const onIsVulnerableChange = React.useCallback(
    (value) => {
      setPersistedState({
        hasChanges: true,
        record: {
          ...record,
          isVulnerable: value,
        },
        errors,
      });
    },
    [record, errors, setPersistedState]
  );

  const onDateOfBirthChange = React.useCallback(
    (value) => {
      setPersistedState({
        hasChanges: true,
        record: {
          ...record,
          dateOfBirth: value,
        },
        errors: {
          ...errors,
          dateOfBirth: undefined,
        },
      });
    },
    [record, errors, setPersistedState]
  );

  // Submit callback.
  const onContinue = React.useCallback(
    (justSave?: boolean, nextSection?: Section) => {
      let futureForm = updatedFormFromPersistedState(form, persistedState);
      const errors = validate(futureForm);

      if (errors) {
        setPersistedState({
          hasChanges,
          record,
          errors,
        });
        if (!nextSection) {
          return;
        }
      }

      setPersistedState((s) => ({ ...s, remoteError: undefined }));

      if (!hasChanges) {
        continueWithoutChanges(Section.Notifier, justSave);
        return;
      }

      futureForm = prepareUpdate(Section.Notifier, futureForm);
      updateNotifier({
        caseId,
        signature,
        record: serverRecordFromPersistedState(persistedState),
      }).then(
        (data) => {
          const person = data.notifier;
          setPersistedState((s) => ({ ...s, hasChanges: false }));
          updateSuccessful(futureForm, Section.Notifier, justSave);
          updatePerson(person);
        },
        (error) => {
          if (error instanceof ValidationError) {
            setPersistedState((s) => ({
              ...s,
              errors: errorsFromServer(error),
              remoteError: undefined,
            }));
          } else {
            setPersistedState((s) => ({
              ...s,
              remoteError: "Operation failed. Please try again or contact customer support.",
            }));
          }
          updateFailure(error);
        }
      );
    },
    [
      caseId,
      form,
      hasChanges,
      record,
      signature,
      setPersistedState,
      updateFailure,
      updatePerson,
      updateSuccessful,
      prepareUpdate,
      continueWithoutChanges,
      persistedState,
    ]
  );

  const removeRemoteError = React.useCallback(() => {
    setPersistedState((s) => ({ ...s, remoteError: undefined }));
  }, [setPersistedState]);

  return (
    <NotifierDetailsView
      busy={busy}
      errors={errors}
      hasChanges={hasChanges}
      remoteError={persistedState.remoteError}
      removeRemoteError={removeRemoteError}
      record={record}
      updatingNotifierEmailAddress={updatingNotifierEmailAddress}
      onContinue={onContinue}
      onDateOfBirthChange={onDateOfBirthChange}
      onFieldChange={onFieldChange}
      onIsVulnerableChange={onIsVulnerableChange}
      onRoleChange={onRoleChange}
      onSectionClick={onSectionClick}
      updateTemplate={updateTemplate}
    />
  );
};

export type NotifierDetailsViewProps = {
  busy: boolean;
  errors: Errors;
  hasChanges: boolean;
  record: NotifierRecord;
  updatingNotifierEmailAddress: boolean;
  remoteError?: string;
  removeRemoteError: () => void;

  onRoleChange: (value: string) => void;
  onDateOfBirthChange: (value?: string) => void;
  onContinue: (exit?: boolean) => void;
  onFieldChange: any;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  onIsVulnerableChange: (value: boolean) => void;
};

export const NotifierDetailsView: React.FC<NotifierDetailsViewProps> = ({
  busy,
  errors,
  hasChanges,

  record,
  remoteError,
  removeRemoteError,

  onRoleChange,
  onDateOfBirthChange,
  onContinue,
  onFieldChange,
  onSectionClick,
  updateTemplate,
  onIsVulnerableChange,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection: "notifier",
      onNext: () => onContinue(false),
      onSave: hasChanges ? () => onContinue(true) : undefined,
      onSectionClick,
    });
  }, [busy, onContinue, onSectionClick, updateTemplate, hasChanges]);

  return (
    <Stack rowGap={4}>
      <Header level={1}>Now, let’s find out more about you.</Header>

      <FormStack>
        <FormField
          halfWidthByItself
          label={
            <span>
              I am a personal representative and I am...{" "}
              <InfoBoxTrigger white width="448px" content={renderRepresentativeInfoBox()} />
            </span>
          }
        >
          <SelectInput
            name="role"
            onValueChange={onRoleChange}
            value={record.role || ""}
            options={roleOptions}
            error={errors?.role}
          />
        </FormField>

        <FormField halfWidthByItself label="Title">
          <NewTitleInput
            name="title"
            onValueChange={(value) => onFieldChange({ target: { name: "title", value } })}
            value={record.title || ""}
            error={errors?.title}
          />
        </FormField>

        <FormField halfWidth label="First name">
          <TextInput
            name="firstName"
            value={record.firstName || ""}
            error={errors?.firstName}
            onChange={onFieldChange}
          />
        </FormField>

        <FormField halfWidth label="Last name">
          <TextInput
            name="surname"
            value={record.surname || ""}
            error={errors?.surname}
            onChange={onFieldChange}
          />
        </FormField>

        {record.role !== "solicitor" && (
          <FormField halfWidthByItself label="Date of birth">
            <DateInput
              value={record.dateOfBirth || ""}
              onValueChange={onDateOfBirthChange}
              error={errors?.dateOfBirth}
              pastOnly
            />
          </FormField>
        )}

        <FormField label="Address">
          <NewAddressInput
            name="address"
            value={record.address || ""}
            onChange={onFieldChange}
            error={errors?.address}
          />
        </FormField>

        <FormField halfWidth label="City or Town">
          <TextInput
            name="city"
            value={record.city || ""}
            onChange={onFieldChange}
            error={errors?.city}
          />
        </FormField>

        <FormField halfWidth label="Postcode">
          <TextInput
            name="postcode"
            value={record.postcode || ""}
            onChange={onFieldChange}
            error={errors?.postcode}
          />
        </FormField>

        <FormField halfWidth label="Telephone number">
          <PhoneNumberTextInput
            name="contactNumber"
            value={record.contactNumber || ""}
            onChange={onFieldChange}
            error={errors?.contactNumber}
          />
        </FormField>

        <FormField halfWidth label="Email address">
          <EmailAddressInput
            name="email"
            value={record.email || ""}
            onChange={onFieldChange}
            error={errors?.email}
            // autoFocus={updatingNotifierEmailAddress}
          />
        </FormField>

        {record.role !== "solicitor" && (
          <FormField>
            <BooleanInput
              label={
                <span>
                  I feel vulnerable (temporarily or long-term) and would appreciate support{" "}
                  <InfoBoxTrigger white width="448px" content={renderVulnerableInfoBox()} />
                </span>
              }
              value={record.isVulnerable}
              onValueChange={onIsVulnerableChange}
            />
          </FormField>
        )}

        {record.role !== "solicitor" && (
          <FormField
            halfWidthByItself
            label={<span>The person who died was my... &nbsp; {renderIntestacyInfoBox()}</span>}
          >
            <RelationToDeceased
              name="relationshipToDeceased"
              value={record.relationshipToDeceased || ""}
              otherValue={record.relationshipToDeceasedOther || ""}
              error={errors.relationshipToDeceased || errors.relationshipToDeceasedOther}
              onFieldChange={onFieldChange}
            />
          </FormField>
        )}
      </FormStack>

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteError}
        autoHideDuration={6000}
        onClose={removeRemoteError}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={removeRemoteError}>
          {remoteError}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

const renderIntestacyInfoBox = () => {
  return (
    <InfoBoxTrigger
      white
      width="720px"
      content={
        <Stack rowGap={2}>
          <Typography variant="body1">
            <b>Please note:</b>
          </Typography>

          <Typography variant="body1">
            <p>
              As per the intestacy rules&nbsp; (when there is no will)&nbsp;, providers require a
              confirmation from the immediate next of kin.&nbsp;&nbsp;Please check who is the next
              of kin in the Family Tree&nbsp; hierarchy&nbsp; here: &nbsp;&nbsp;
              <a href="https://www.settld.care/what-if-theres-no-will-intestacy-rules/">
                What if there is no will
              </a>
            </p>
          </Typography>
          <Typography variant="body1">
            If you're not the immediate next of kin, please provide their full name, relation to
            deceased and address in the messages section of your dashboard, once the form is
            submitted.
          </Typography>
        </Stack>
      }
    />
  );
};

const renderVulnerableInfoBox = () => {
  return (
    <Stack rowGap={2}>
      <Typography variant="body1">
        <b>What help is available?</b>
      </Typography>

      <Typography variant="body1">
        After a bereavement, many people find they require support with mental or physical health,
        debts or managing money, and other sensitive matters. Banks, Insurers, Utilities and other
        organisations can help, but only if people ask for it.
      </Typography>
      <Typography variant="body1">
        If you select ‘I feel vulnerable’, Settld will alert all companies transferring accounts
        into your name. The organisations will then follow up with you directly, using the contact
        details you provide.
      </Typography>
    </Stack>
  );
};

const renderRepresentativeInfoBox = () => {
  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Typography variant="body1">
          <b>What is a "personal representative"?</b>
        </Typography>

        <Typography variant="body1">
          A <b>personal representative</b> is the person who is legally entitled to administer the
          estate of the person who has died. This term is used to collectively refer to both{" "}
          <b>Executors</b> and
          <b>Administrators</b>.
        </Typography>
      </Stack>

      <Stack rowGap={2}>
        <Typography variant="body1">
          <b>Am I an "Executor" or an "Administrator"?</b>
        </Typography>

        <Typography variant="body1">
          Use the diagram below to determine what type of personal representative you are.
        </Typography>
      </Stack>

      <Box sx={{ alignSelf: "center" }}>
        <img
          src={personalRepresentative}
          alt="personal representative diagram"
          width="400"
          height="auto"
          style={{ maxWidth: "100%" }}
        />
      </Box>
    </Stack>
  );
};

const requiredFields: ReadonlyArray<keyof NotifierDetailsType> = [
  "relationshipToDeceased",
  "title",
  "firstName",
  "surname",
  "address",
  "city",
  "postcode",
  "contactNumber",
  "email",
  "role",
];

const validate = (form: NotifierFormData): Errors | undefined => {
  let errors: Errors = {};

  if (!validEmailAddress(form.notifier?.email || "")) {
    errors.email = "Not a valid email address";
  }

  requiredFields.forEach((key) => {
    if (!form.notifier || !form.notifier[key]) {
      errors = { ...errors, [key]: "required" };
    }
  });

  if (!form.notifier || (!form.notifier.dateOfBirth && form.notifier.role !== "solicitor")) {
    errors = { ...errors, dateOfBirth: "required" };
  }

  if (
    form.notifier?.relationshipToDeceased === Relationship.Other &&
    !form.notifier?.relationshipToDeceasedOther
  ) {
    errors = { ...errors, relationshipToDeceasedOther: "required" };
  }

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export const isComplete = (form: NotifierFormData): boolean => validate(form) === undefined;

class ValidationError extends Error {
  errors: { [k: string]: string[] };

  constructor(errors: { [k: string]: string[] }) {
    super("validation error");
    this.errors = errors;
    this.name = "ValidationError";
  }
}

const errorsFromServer = (ve: ValidationError) => {
  const errors = {
    firstName: ve.errors.firstName ? ve.errors.firstName[0] : undefined,
    lastName: ve.errors.lastName ? ve.errors.lastName[0] : undefined,
    email: ve.errors.email ? ve.errors.email[0] : undefined,
  };

  return JSON.parse(JSON.stringify(errors));
};
